import { Card, TextField } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useFormikContext } from "formik";
import { useState } from "react";

import MDBox from "@/components/atoms/MDBox";
import { MDButton } from "@/components/atoms/MDButton";
import IVariation from "@/interfaces/variation.interface";
import { VariationFilterMessages } from "@/messages/variation/variations-filter.messages";
import { FilterType, FilterValue, IFilter } from "@/types/filter.types";

import { VariationFilterKey } from "../../types";

// Define the variation filter interface
export interface IVariationFilter {
  property: VariationFilterKey;
  value: FilterValue;
  filterType: FilterType;
}

type VariationFiltersFormProps = {
  messages: VariationFilterMessages;
  temporaryFilters: IFilter<IVariation>[];
  addNewTemporaryFilter: (filter: IFilter<IVariation>) => void;
  deleteTemporaryFilter: (index: number) => void;
};

export default function VariationFiltersForm({
  messages,
  temporaryFilters,
  addNewTemporaryFilter,
  deleteTemporaryFilter,
}: VariationFiltersFormProps) {
  const { values, setValues, submitForm, errors } = useFormikContext<IVariationFilter[]>();
  const [nameValue, setNameValue] = useState<string>("");

  const handleChange = (property: VariationFilterKey, value: FilterValue) => {
    // Find if a filter for this property already exists
    const existingFilterIndex = values.findIndex((filter) => filter.property === property);

    let updatedValues: IVariationFilter[];

    if (existingFilterIndex >= 0) {
      // Update the existing filter
      updatedValues = [...values];
      updatedValues[existingFilterIndex] = {
        property,
        value,
        filterType: FilterType.Contains,
      };
    } else {
      // Add a new filter while keeping the existing ones
      updatedValues = [...values, { property, value, filterType: FilterType.Contains }];
    }

    setValues(updatedValues);
  };

  const handleSubmit = () => {
    // Add all valid filters to temporaryFilters
    values.forEach((filter) => {
      if (
        filter.value &&
        !temporaryFilters.some((tempFilter) => tempFilter.property === filter.property)
      ) {
        addNewTemporaryFilter({
          ...filter,
          label: messages.name,
        } as IFilter<IVariation>);
      }
    });
    submitForm();
  };

  const handleClear = () => {
    temporaryFilters.forEach((_, index) => deleteTemporaryFilter(index));
    setValues([]);
    setNameValue("");
  };

  const getErrorFromProperty = (property: VariationFilterKey) => {
    if (!errors || !Array.isArray(errors)) return "";
    const error = errors.find((error) => error?.property === property);
    return error?.value || "";
  };

  const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setNameValue(value);
    handleChange("description", value);
  };

  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleSubmit();
    }
  };

  return (
    <Card>
      <MDBox p={2}>
        <Grid container spacing={2} alignItems="stretch" wrap="wrap" justifyContent="space-between">
          <Grid size={{ lg: 8, md: 8, sm: 6, xs: 12 }}>
            <TextField
              fullWidth
              label={messages.name}
              value={nameValue}
              onChange={handleNameChange}
              onKeyDown={handleKeyPress}
              slotProps={{ inputLabel: { shrink: true } }}
              error={Boolean(getErrorFromProperty("description"))}
              helperText={getErrorFromProperty("description")}
            />
          </Grid>

          <Grid size={{ lg: 2, md: 2, sm: 6, xs: 12 }}>
            <MDButton variant="gradient" color="light" onClick={handleClear} fullWidth>
              {messages.clearFilters}
            </MDButton>
          </Grid>

          <Grid size={{ lg: 2, md: 2, sm: 6, xs: 12 }}>
            <MDButton variant="gradient" color="dark" onClick={handleSubmit} fullWidth>
              {messages.filter}
            </MDButton>
          </Grid>
        </Grid>
      </MDBox>
    </Card>
  );
}
